import { useMutation } from "@tanstack/react-query";
import isWithinStudyArea from "../constants/studyArea";
import i18n from "../i18n";

// -------------------------------------------------------------
// useGpsSearch
// Takes raw latitude and longitude entered by the user,
// validates them against the study area boundary, then
// calls Nominatim's reverse geocoding API to turn the
// coordinates into a human-readable location name.
//
// Why useMutation and not useQuery?
// - useQuery runs automatically when a component mounts
// - this search should only run when the user submits GPS coordinates
// - useMutation gives us manual control over when the request fires
//
// Why mutateAsync and not mutate?
// - mutate is fire-and-forget — you can't await it
// - mutateAsync returns a promise, so we can await it in handleSearch
// - this matters because handleSearch needs to wait for the GPS lookup
//   to finish before the result panel can show anything meaningful
//
// Why wrap mutateAsync in searchByGps?
// - mutateAsync throws loudly if something goes wrong
// - without a wrapper, that error flies straight into handleSearch
//   which has no try/catch — crashing the app
// - the wrapper quietly catches the error here so handleSearch never
//   sees it. React Query already stores the error in the error state
//   which the component reads and shows as a toast message
//
// Returns:
// - searchByGps: function to trigger the GPS lookup
// - coordinates: the validated { lat, lng } object
// - locationName: the resolved human-readable place name
// - isLoading: true while the request is in progress
// - error: error message if something went wrong
// - reset: clears all state back to initial
// -------------------------------------------------------------

// Handles GPS coordinate validation and reverse geocoding.
const fetchGpsSearch = async ({ lat, lng }) => {
  const latitude = Number(lat);
  const longitude = Number(lng);

  // Make sure the values are actual numbers before going any further.
  if (Number.isNaN(latitude) || Number.isNaN(longitude)) {
    throw new Error(`⚠️ ${i18n.t("locationAndGps.latLonError")}`);
  }

  // Reject coordinates that fall outside the Kaduna/Kano study area.
  if (!isWithinStudyArea(latitude, longitude)) {
    throw new Error(`⚠️ ${i18n.t("locationAndGps.coordinatesOutsideStudyArea")}`);
  }

  try {
    // Send the coordinates to Nominatim to get a human-readable place name.
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&addressdetails=1&accept-language=en`,
    );

    if (!response.ok) {
      throw new Error(`⚠️ ${i18n.t("locationAndGps.failedToResolveCoordinates")}`);
    }

    const data = await response.json();
    const address = data?.address ?? {};
    const localName =
      address.village || address.town || address.city || address.county || "Unknown area";
    const state = address.state || "Unknown state";
    const country = address.country || "Unknown country";

    return {
      coordinates: { lat: latitude, lng: longitude },
      locationName: `${localName}, ${state}, ${country}`,
    };
  } catch (err) {
    // Nominatim throttling or temporary blocks surface as a generic network error.
    if (err instanceof Error && err.message === "Failed to fetch") {
      throw new Error(`⚠️ ${i18n.t("locationAndGps.locationServiceBusy")}`);
    }

    if (err instanceof Error) {
      throw err;
    }

    // Fallback: if Nominatim fails entirely, return the raw coordinates as the name
    // so the user still sees something meaningful instead of a crash.
    return {
      coordinates: { lat: latitude, lng: longitude },
      locationName: `Lat ${latitude.toFixed(4)}, Lon ${longitude.toFixed(4)}`,
    };
  }
};

const useGpsSearch = () => {
  // mutateAsync is used over mutate because handleSearch needs to await
  // the result — it has to wait for the GPS lookup to finish before the
  // result panel can show anything. Like standing there waiting for a
  // reply before moving on.
  const { mutateAsync, data, error, isPending, reset } = useMutation({
    mutationFn: fetchGpsSearch,
  });

  // Wraps mutateAsync so any errors are caught here and never reach the
  // caller. React Query already stores the error in the error state above,
  // so the component just reads that and shows it as a toast — no crash.
  const searchByGps = async ({ lat, lng }) => {
    try {
      await mutateAsync({ lat, lng });
    } catch (err) {
      return;
    }
  };

  return {
    searchByGps,
    coordinates: data?.coordinates ?? null,
    locationName: data?.locationName ?? null,
    isLoading: isPending,
    error: error?.message ?? null,
    reset,
  };
};

export default useGpsSearch;
